import { refresh, sendCode, verifyEmail } from "../auth/asyncThunks";
import {
  generateAnonymize,
  generateCollection,
  generateImageCollection,
  regenerateCollection,
  regenerateImageCollection,
  toggleToFavorite,
  updateTrainModel,
} from "../model/asyncThunks";
import { toggleFavoritePlaylist } from "../playlist/asyncThunk";
import {
  addDailyCredits,
  deleteAccount,
  fetchUserInfo,
  changeUsername,
  getReferralUsers,
  spinTheWheel,
  getSameAuthorCollections,
  submitRequest,
  getUserData,
  getUserCollectionsData,
  updateProfile,
  getFollowers,
  getFollowing,
  toggleFollow,
} from "./asyncThunk";

const getUserId = (user) => user?._id || user?.id;

const isSameUser = (user, userId) => getUserId(user) === userId;

const findUserInList = (list, userId) => {
  if (!Array.isArray(list)) {
    return null;
  }

  return list.find((user) => isSameUser(user, userId)) || null;
};

const applyFollowStateToList = (list, userId, isFollowing) => {
  if (!Array.isArray(list)) {
    return;
  }

  const idx = list.findIndex((user) => isSameUser(user, userId));
  if (idx !== -1) {
    list[idx] = { ...list[idx], isFollowing };
  }
};

const getCurrentFollowState = (state, userId) => {
  if (isSameUser(state.profileUser?.data, userId)) {
    return !!state.profileUser.data?.isFollowing;
  }

  const matchingUser =
    findUserInList(state.followers.data, userId) ||
    findUserInList(state.following.data, userId);

  return !!matchingUser?.isFollowing;
};

const applyFollowState = (state, userId, isFollowing) => {
  applyFollowStateToList(state.followers.data, userId, isFollowing);
  applyFollowStateToList(state.following.data, userId, isFollowing);

  if (isSameUser(state.profileUser?.data, userId)) {
    state.profileUser.data = {
      ...state.profileUser.data,
      isFollowing,
      isFollowed: isFollowing,
    };
  }
};

export const fetchUserInfoBuilder = (builder) => {
  builder.addCase(fetchUserInfo.pending, (state) => {
    state.fetchUserInfoPending = true;
  });
  builder.addCase(fetchUserInfo.fulfilled, (state, { payload }) => {
    state.user = payload.user;
    state.fetchUserInfoPending = false;
  });
  builder.addCase(fetchUserInfo.rejected, (state) => {
    state.user = undefined;
    state.fetchUserInfoPending = false;
  });
};

export const sendCodeBuilder = (builder) => {
  builder.addCase(sendCode.fulfilled, (state, { payload }) => {
    const { user } = payload;
    state.user = user;
  });
};

export const verifyEmailBuilder = (builder) => {
  builder.addCase(verifyEmail.fulfilled, (state, { payload }) => {
    const { user } = payload.data;
    state.user = user;
  });
};

export const generateCollectionBuilder = (builder) => {
  builder.addCase(generateCollection.fulfilled, (state, { payload }) => {
    const { credits } = payload.data;
    state.user.credits = credits;
  });
};

export const regenerateCollectionBuilder = (builder) => {
  builder.addCase(regenerateCollection.fulfilled, (state, { payload }) => {
    const { credits } = payload.data;
    state.user.credits = credits;
  });
};

export const generateEditImageCollectionBuilder = (builder) => {
  builder.addCase(generateImageCollection.fulfilled, (state, { payload }) => {
    const { credits } = payload.data;
    state.user.credits = credits;
  });
};

export const regenerateEditImageCollectionBuilder = (builder) => {
  builder.addCase(regenerateImageCollection.fulfilled, (state, { payload }) => {
    const { credits } = payload.data;
    state.user.credits = credits;
  });
};

export const generateAnonymizeCollectionBuilder = (builder) => {
  builder.addCase(generateAnonymize.fulfilled, (state, { payload }) => {
    state.user.credits = parseInt(state.user.credits) - 5;
  });
};

export const addDailyCreditsBuilder = (builder) => {
  builder.addCase(addDailyCredits.pending, (state) => {
    state.addDailyCreditsPending = true;
  });
  builder.addCase(addDailyCredits.fulfilled, (state, { payload }) => {
    const { credits, lastFreeCredits } = payload;

    state.user.credits = credits;
    state.user.lastFreeCredits = lastFreeCredits;
    state.addDailyCreditsPending = false;
  });
  builder.addCase(addDailyCredits.rejected, (state) => {
    state.addDailyCreditsPending = false;
  });
};

export const spinTheWheelBuilder = (builder) => {
  builder.addCase(spinTheWheel.pending, (state) => {
    state.spinTheWheelPending = true;
  });
  builder.addCase(spinTheWheel.fulfilled, (state, { payload }) => {
    const { lastFreeCredits } = payload;

    // state.user.credits = credits;
    state.user.lastFreeCredits = lastFreeCredits;
    state.spinTheWheelPending = false;
  });
  builder.addCase(spinTheWheel.rejected, (state) => {
    state.spinTheWheelPending = false;
  });
};

export const setUsernameBuilder = (builder) => {
  builder.addCase(changeUsername.pending, (state) => {
    state.setUsernamePending = true;
  });
  builder.addCase(changeUsername.fulfilled, (state, { payload }) => {
    const { username, affiliateLink } = payload;
    state.user.username = username;
    state.user.affiliateLink = affiliateLink;
    state.setUsernamePending = false;
  });
  builder.addCase(changeUsername.rejected, (state) => {
    state.setUsernamePending = false;
  });
};

export const deleteAccountBuilder = (builder) => {
  builder.addCase(deleteAccount.pending, (state) => {
    state.deleteAccountPending = true;
  });
  builder.addCase(deleteAccount.fulfilled, (state) => {
    state.deleteAccountPending = false;
  });
  builder.addCase(deleteAccount.rejected, (state) => {
    state.deleteAccountPending = false;
  });
};

export const getReferralUsersBuilder = (builder) => {
  builder.addCase(getReferralUsers.pending, (state) => {
    state.getReferralUsersPending = true;
  });
  builder.addCase(getReferralUsers.fulfilled, (state, { payload }) => {
    const { data } = payload;

    state.referralEarnings.data = data;

    // state.referralEarnings.maxReached = maxReached;
    // state.referralEarnings.perPage = +perPage;
    // state.referralEarnings.page = +page;
    // state.referralEarnings.total = +total;
    state.getReferralUsersPending = false;
  });
  builder.addCase(getReferralUsers.rejected, (state) => {
    state.getReferralUsersPending = false;
  });
};

export const updateTrainModelBuilder = (builder) => {
  builder.addCase(updateTrainModel.fulfilled, (state, { payload }) => {
    const { credits } = payload.data;
    state.user.credits = credits;
    state.user.hasTrainedModel = true;
  });
};

export const toggleToFavoriteBuilder = (builder) => {
  builder.addCase(toggleToFavorite.pending, (state, { meta }) => {
    const { collectionId } = meta.arg;
    if (!state.user.favoriteCollections.includes(collectionId)) {
      state.user.favoriteCollections.push(collectionId);
    } else {
      state.user.favoriteCollections = state.user.favoriteCollections.filter(
        (c) => c !== collectionId,
      );
    }
  });
  builder.addCase(toggleToFavorite.rejected, (state, { meta }) => {
    const { collectionId } = meta.arg;
    if (!state.user.favoriteCollections.includes(collectionId)) {
      state.user.favoriteCollections.push(collectionId);
    } else {
      state.user.favoriteCollections = state.user.favoriteCollections.filter(
        (c) => c !== collectionId,
      );
    }
  });
};

export const toggleToFavoritePlaylistBuilder = (builder) => {
  builder.addCase(toggleFavoritePlaylist.pending, (state, { meta }) => {
    const { playlistId } = meta.arg;
    if (state.user?.favoritePlaylists) {
      if (!state.user.favoritePlaylists.includes(playlistId)) {
        state.user.favoritePlaylists.push(playlistId);
      } else {
        state.user.favoritePlaylists = state.user.favoritePlaylists.filter(
          (c) => c !== playlistId,
        );
      }
    } else {
      state.user.favoritePlaylists = [playlistId];
    }
  });
};

export const refreshBuilder = (builder) => {
  builder.addCase(refresh.rejected, (state, action) => {
    state.user = undefined;
  });
};

export const getSameAuthorCollectionsBuilder = (builder) => {
  builder.addCase(getSameAuthorCollections.pending, (state) => {
    state.sameAuthorCollections.loading = true;
  });
  builder.addCase(getSameAuthorCollections.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.sameAuthorCollections.data = data;
    state.sameAuthorCollections.loading = false;
  });
  builder.addCase(getSameAuthorCollections.rejected, (state) => {
    state.sameAuthorCollections.loading = false;
    state.sameAuthorCollections.error = true;
  });
};

export const submitRequestBuilder = (builder) => {
  builder.addCase(submitRequest.pending, (state) => {
    state.submitRequestLoading = true;
  });
  builder.addCase(submitRequest.fulfilled, (state) => {
    state.submitRequestLoading = false;
  });
  builder.addCase(submitRequest.rejected, (state) => {
    state.submitRequestLoading = false;
  });
};

export const getFollowersBuilder = (builder) => {
  builder.addCase(getFollowers.pending, (state) => {
    state.followers.loading = true;
    state.followers.error = null;
    state.followers.data = [];
    state.followers.page = 1;
    state.followers.maxReached = false;
  });
  builder.addCase(getFollowers.fulfilled, (state, { payload }) => {
    state.followers.data = payload?.data ?? [];
    state.followers.page = payload?.page ?? 1;
    state.followers.perPage = payload?.perPage ?? state.followers.perPage;
    state.followers.maxReached = !!payload?.maxReached;
    state.followers.loading = false;
  });
  builder.addCase(getFollowers.rejected, (state, { payload }) => {
    state.followers.loading = false;
    state.followers.error = payload || true;
  });
};

export const getFollowingBuilder = (builder) => {
  builder.addCase(getFollowing.pending, (state) => {
    state.following.loading = true;
    state.following.error = null;
    state.following.data = [];
    state.following.page = 1;
    state.following.maxReached = false;
  });
  builder.addCase(getFollowing.fulfilled, (state, { payload }) => {
    state.following.data = payload?.data ?? [];
    state.following.page = payload?.page ?? 1;
    state.following.perPage = payload?.perPage ?? state.following.perPage;
    state.following.maxReached = !!payload?.maxReached;
    state.following.loading = false;
  });
  builder.addCase(getFollowing.rejected, (state, { payload }) => {
    state.following.loading = false;
    state.following.error = payload || true;
  });
};

export const toggleFollowBuilder = (builder) => {
  builder.addCase(toggleFollow.pending, (state, { meta }) => {
    const userId = meta.arg;
    const previousIsFollowing = getCurrentFollowState(state, userId);
    const nextIsFollowing = !previousIsFollowing;

    state.toggleFollowPending[userId] = {
      previousCurrentUserFollowingCount: state.user?.numOfFollowing,
      previousFollowerCount: isSameUser(state.profileUser?.data, userId)
        ? state.profileUser.data?.numOfFollowers
        : undefined,
      previousIsFollowing,
      requestId: meta.requestId,
    };

    applyFollowState(state, userId, nextIsFollowing);

    if (isSameUser(state.profileUser?.data, userId)) {
      const currentFollowers = Number(state.profileUser.data?.numOfFollowers ?? 0);
      state.profileUser.data.numOfFollowers = Math.max(
        0,
        currentFollowers + (nextIsFollowing ? 1 : -1),
      );
    }

    if (state.user?.numOfFollowing !== undefined) {
      state.user.numOfFollowing = Math.max(
        0,
        Number(state.user.numOfFollowing ?? 0) + (nextIsFollowing ? 1 : -1),
      );
    }
  });
  builder.addCase(toggleFollow.fulfilled, (state, { meta, payload }) => {
    const userId = meta.arg;
    const pendingState = state.toggleFollowPending[userId];
    delete state.toggleFollowPending[userId];

    const data = payload?.data ?? {};
    const isFollowing =
      typeof data.isFollowing === "boolean"
        ? data.isFollowing
        : pendingState?.previousIsFollowing !== undefined
          ? !pendingState.previousIsFollowing
          : getCurrentFollowState(state, userId);

    applyFollowState(state, userId, isFollowing);

    if (isSameUser(state.profileUser?.data, userId)) {
      state.profileUser.data = {
        ...state.profileUser.data,
        isFollowing,
        isFollowed: isFollowing,
        numOfFollowers:
          data.user?.numOfFollowers ??
          state.profileUser.data.numOfFollowers ??
          0,
      };
    }

    if (state.user && data.currentUser?.numOfFollowing !== undefined) {
      state.user.numOfFollowing = data.currentUser.numOfFollowing;
    }
  });
  builder.addCase(toggleFollow.rejected, (state, { meta }) => {
    const userId = meta.arg;
    const pendingState = state.toggleFollowPending[userId];
    delete state.toggleFollowPending[userId];

    if (!pendingState) {
      return;
    }

    applyFollowState(state, userId, pendingState.previousIsFollowing);

    if (
      isSameUser(state.profileUser?.data, userId) &&
      pendingState.previousFollowerCount !== undefined
    ) {
      state.profileUser.data.numOfFollowers = pendingState.previousFollowerCount;
    }

    if (pendingState.previousCurrentUserFollowingCount !== undefined && state.user) {
      state.user.numOfFollowing = pendingState.previousCurrentUserFollowingCount;
    }
  });
};

export const updateProfileBuilder = (builder) => {
  builder.addCase(updateProfile.pending, (state) => {
    state.updateProfilePending = true;
  });
  builder.addCase(updateProfile.fulfilled, (state, { payload }) => {
    const updated = payload?.data?.user ?? payload?.user ?? payload?.data;
    if (updated && state.user) {
      state.user = { ...state.user, ...updated };
    }
    state.updateProfilePending = false;
  });
  builder.addCase(updateProfile.rejected, (state) => {
    state.updateProfilePending = false;
  });
};

export const getUserDataBuilder = (builder) => {
  builder.addCase(getUserData.pending, (state, { meta }) => {
    const requestedUserId = meta.arg;
    const currentUserId =
      state.profileUser.data?._id || state.profileUser.data?.id;

    if (requestedUserId && currentUserId && requestedUserId !== currentUserId) {
      state.profileUser.data = null;
      state.profileUser.collections = [];
      state.profileUser.page = 1;
      state.profileUser.perPage = 20;
      state.profileUser.total = 0;
      state.profileUser.maxReached = false;
    }

    state.profileUser.loading = true;
    state.profileUser.error = null;
  });
  builder.addCase(getUserData.fulfilled, (state, { payload }) => {
    const data = payload?.data ?? payload;
    const collections = data?.collections ?? [];
    state.profileUser.data = data?.user ?? null;
    state.profileUser.collections = collections;
    state.profileUser.page = 1;
    state.profileUser.perPage = collections.length || state.profileUser.perPage;
    state.profileUser.total = collections.length;
    state.profileUser.maxReached = true;
    state.profileUser.loading = false;
  });
  builder.addCase(getUserData.rejected, (state, { payload }) => {
    state.profileUser.data = null;
    state.profileUser.collections = [];
    state.profileUser.page = 1;
    state.profileUser.perPage = 20;
    state.profileUser.total = 0;
    state.profileUser.maxReached = false;
    state.profileUser.loading = false;
    state.profileUser.error = payload || true;
  });
};

export const getUserCollectionsDataBuilder = (builder) => {
  builder.addCase(getUserCollectionsData.pending, (state, { meta }) => {
    const requestedUsername = meta.arg?.username;
    const currentUsername = state.profileUser.data?.username;

    if (
      requestedUsername &&
      currentUsername &&
      requestedUsername !== currentUsername
    ) {
      state.profileUser.data = null;
      state.profileUser.collections = [];
      state.profileUser.page = 1;
      state.profileUser.perPage = 20;
      state.profileUser.total = 0;
      state.profileUser.maxReached = false;
    }

    state.profileUser.loading = true;
    state.profileUser.error = null;
  });
  builder.addCase(getUserCollectionsData.fulfilled, (state, { payload }) => {
    const response = payload ?? {};
    const data = response?.data ?? {};
    const collections =
      data?.collections ??
      response?.collections ??
      response?.data?.data?.collections ??
      [];
    const userData =
      data?.user ?? response?.user ?? response?.data?.data?.user ?? null;

    state.profileUser.data = userData;
    state.profileUser.collections = collections;
    state.profileUser.page =
      response?.page ?? data?.page ?? state.profileUser.page;
    state.profileUser.perPage =
      response?.perPage ??
      data?.perPage ??
      state.profileUser.perPage ??
      collections.length;
    state.profileUser.total =
      response?.total ?? data?.total ?? collections.length;
    state.profileUser.maxReached = !!(
      response?.maxReached ??
      data?.maxReached ??
      state.profileUser.maxReached
    );
    state.profileUser.loading = false;
  });
  builder.addCase(getUserCollectionsData.rejected, (state, { payload }) => {
    state.profileUser.data = null;
    state.profileUser.collections = [];
    state.profileUser.page = 1;
    state.profileUser.perPage = 20;
    state.profileUser.total = 0;
    state.profileUser.maxReached = false;
    state.profileUser.loading = false;
    state.profileUser.error = payload || true;
  });
};
